<template>
  <div v-if="showPage" class="container pap-form">
    <!-- Note: Without the uploadCompleted in the v-if, alert card is shown at end of the max'th upload then replaced by the 
    uploadResult card (since at that time, max num uploads has been reached making showMaxUploadsReachedAlert 'true'). 
    WITH uploadCompleted, this alert card is NOT 'flashed' -- cleaner! -->
    <AlertCard
      v-if="!uploadCompleted && showMaxUploadsReachedAlert"
      type="error"
      header="Oops! You cannot upload an image at the moment..."
    >
      <p>
        Look at Your Images summary panel above. The number of images in your
        Uploads list has reached it's maximum.
      </p>
      <p>You can free up an Upload slot by deleting one of your uploads.</p>
      <p>
        Also, for every Upload image we Select for a book or determine to be
        Unusable (e.g. image too dark, out of focus, etc.), an Upload slot will
        become available since the Selected and/or Unusable images will be moved
        to their respective lists.
      </p>
      <!-- <p class="mt-2 mb-0"><strong>Caption:</strong></p>
      <p>{{ caption }}</p> -->
    </AlertCard>

    <AlertCard
      v-if="showUploadResult && resultType === 'error'"
      :type="resultType"
      :header="resultHeader"
      :message="resultMessage"
    >
      <p class="mt-2 mb-0"><strong>Caption:</strong></p>
      <p>{{ caption }}</p>
    </AlertCard>

    <div
      v-if="showUploadResult && resultType === 'success'"
      class="wrapper--center fw-300"
    >
      <CardImage
        :image="uploadedImage"
        header-type="alert"
        alert-header="Upload Successful!"
        type="pending"
        :showHeader="true"
        :showButtonPanel="false"
        :showProductsPanel="false"
      />
    </div>

    <div v-if="showForm" class="card">
      <FormTitle
        title="Upload New Image"
        iconclass="fas fa-cloud-upload-alt mr-2"
      />

      <div class="card-body">
        <b-form novalidate @submit.prevent="doUpload">
          <!-- -------------------------------- Accept Terms checkbox  -->
          <b-form-group
            id="accept-terms"
            label="Agree To Terms"
            label-class="label-bold"
          >
            <b-form-checkbox
              id="agree-terms"
              v-model="agreeTerms"
              name="agree-terms"
              :value="true"
              :unchecked-value="false"
              @change="toggleAgreeTerms"
            >
              <!-- <p class="text-smaller-1 mb-1">
                I agree to the Pixapup Terms for the use of my dog's image
              </p> -->
              <p class="pap-form__text m-0">
                I agree to the Pixapup Terms for the use of my dog's image
              </p>
            </b-form-checkbox>
            <p class="text-center text-smaller-1">
              <a href="#" v-b-modal.modal-tos>See Terms of Service</a>
            </p>
          </b-form-group>

          <!-- -------------------------------- Image  -->

          <ImageFileField
            id="main-image"
            label="Dog Image"
            placeholder="Choose an image..."
            @image-selected="imageSelected($event)"
            :state="form.imageFilename.valid"
            :errorMsg="form.imageFilename.errorMsg"
            :disabled="form.imageFilename.disabled"
          />
          <p class="text-center text-smaller-1">
            <a href="#" v-b-modal.modal-image-guide
              >Image Guide: What We're Looking For</a
            >
          </p>

          <div
            class="pap-form__section pap-form__section--light-gray pap-form__section--mb-2"
          >
            <!-- <b-form-group label="One-Click Form Fill" label-class="label-bold"> -->

            <div class="text-dark">
              <p class="label-bold mb-1">One-Click Form Fill</p>
              <p class="text-smaller-1 mt-0 mb-2 mx-2">
                Quickly fill in the fields below with values from your User
                Profile Form Preferences by clicking the button below. You can
                still edit the fields after the One-Click fill.
              </p>

              <div class="text-center">
                <button
                  class="btn btn-primary btn-sm"
                  :disabled="formFillDisabled"
                  @click.prevent="fillForm"
                >
                  <i class="fas fa-align-left text-smaller-2"></i> Click To Fill
                  Form
                </button>
                <p class="text-smaller-1 mt-2 mb-0">
                  <router-link to="/profile"
                    >Fill in your Form Preferences here.</router-link
                  >
                </p>
              </div>
            </div>

            <!-- </b-form-group> -->
          </div>

          <!-- -------------------------------- Dog name  -->
          <b-form-group
            id="dogName-group"
            label="Dog Name"
            label-for="dogName"
            label-class="label-bold"
            :invalid-feedback="form.dogName.errorMsg"
            :state="form.dogName.valid"
          >
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 mb-1">
              List names of <strong>all</strong> dogs in the order in which they
              appear in the image.
            </p>
            <b-form-input
              id="dogName"
              :maxlength="fieldMaxLength('dogName')"
              placeholder="Dog Name"
              @input="fieldOnInput('dogName')"
              v-model="form.dogName.value"
              :state="form.dogName.valid"
              trim
              :disabled="form.dogName.disabled"
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Breed (breedId) -->
          <SelectBreed
            id="breed-id"
            label="Breed"
            :selected="form.breedId.value"
            :state="form.breedId.valid"
            :errorMsg="form.breedId.errorMsg"
            @breed-selected="setBreed($event)"
            :disabled="form.breedId.disabled"
          />

          <!-- -------------------------------- Breed Desc   **CONDITIONAL if breedId is 'mixed' -->

          <b-form-group
            v-show="form.breedId.value === 'mixed'"
            id="breed-desc-group"
            label="Breed Description"
            label-for="breedDesc"
            label-class="label-bold"
            :invalid-feedback="form.breedDesc.errorMsg"
            :state="form.breedDesc.valid"
          >
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
              For <strong>Breed</strong> you selected 'Mixed' - Briefly describe
              your mixed breed.
            </p>
            <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
              If more than one breed is represented in the photo (example: two
              dogs in pic, each is its own breed), list the breed of each dog
              here. You <strong>must</strong> use this field; if you need more
              space, continue your entry in the Note field below.
            </p>
            <b-form-input
              id="breed-desc"
              :maxlength="fieldMaxLength('breedDesc')"
              placeholder="Describe your mixed breed"
              @input="fieldOnInput('breedDesc')"
              v-model="form.breedDesc.value"
              :state="form.breedDesc.valid"
              :disabled="form.breedDesc.disabled"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Country  -->
          <SelectCountry
            id="country"
            label="Country / Territory"
            :selected="form.country.value"
            :state="form.country.valid"
            :errorMsg="form.country.errorMsg"
            @country-selected="setField($event, 'country')"
            :disabled="form.country.disabled"
          />

          <!-- -------------------------------- State   **CONDITIONAL if country is US -->
          <SelectState
            v-if="form.country.value === 'US'"
            id="state"
            label="State"
            :selected="form.state.value"
            :state="form.state.valid"
            :errorMsg="form.state.errorMsg"
            @state-selected="setField($event, 'state')"
            :disabled="form.state.disabled"
          />

          <!-- ------------------------ Province  **CONDITIONAL if country is *not* US -->
          <ProvinceField
            v-show="
              form.country.value !== 'US' &&
              form.country.value !== '' &&
              form.country.value !== '-'
            "
            id="province"
            :maxlength="fieldMaxLength('province')"
            label="Province / Region"
            placeholder="Province / Region"
            :key="componentKey"
            :init="form.province.value"
            :state="form.province.valid"
            :errorMsg="form.province.errorMsg"
            @province-value-input="setField($event, 'province')"
            :disabled="form.province.disabled"
          />

          <!-- v-model="form.province.value" -->

          <!-- -------------------------------- City -->
          <b-form-group
            id="city-group"
            label="City"
            label-for="city"
            label-class="label-bold"
            :invalid-feedback="form.city.errorMsg"
            :state="form.city.valid"
          >
            <b-form-input
              id="city"
              :maxlength="fieldMaxLength('city')"
              placeholder="City"
              @input="fieldOnInput('city')"
              v-model="form.city.value"
              :state="form.city.valid"
              :disabled="form.city.disabled"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- First name  -->
          <b-form-group
            id="fName-group"
            label="Your First Name"
            label-for="fName"
            label-class="label-bold"
            :invalid-feedback="form.fName.errorMsg"
            :state="form.fName.valid"
          >
            <b-form-input
              id="fName"
              :maxlength="fieldMaxLength('fName')"
              placeholder="First Name"
              @input="fieldOnInput('fName')"
              v-model="form.fName.value"
              :state="form.fName.valid"
              :disabled="form.fName.disabled"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Last name  -->
          <b-form-group
            id="lName-group"
            label="Your Last Name"
            label-for="lName"
            label-class="label-bold"
            :invalid-feedback="form.lName.errorMsg"
            :state="form.lName.valid"
          >
            <b-form-input
              id="lName"
              :maxlength="fieldMaxLength('lName')"
              placeholder="Last Name"
              @input="fieldOnInput('lName')"
              v-model="form.lName.value"
              :state="form.lName.valid"
              :disabled="form.lName.disabled"
              trim
            ></b-form-input>
          </b-form-group>

          <!-- -------------------------------- Caption Info  -->
          <CaptionSection
            id="caption"
            label="Image Caption"
            :form="form"
            @caption-last-name-toggle="caption = $event"
          />

          <!-- -------------------------------- Special Project Code  -->

          <b-form-group
            id="specProjCode-group"
            label-for="specProjCode"
            label-class="label-bold"
            :invalid-feedback="form.specProjCode.errorMsg"
            :state="form.specProjCode.valid"
          >
            <slot name="label">
              <ToggleControl toggleId="specialProjectCode">
                <span class="label-bold">Special Project Code (optional)</span>
              </ToggleControl>
            </slot>
            <b-collapse id="specialProjectCode">
              <div class="mx-3 my-2">
                <ToggleControl
                  toggle-id="specialProjectCodeText"
                  closed-text="Learn more..."
                  open-text="- hide -"
                  toggle-class="text-secondary mb-2"
                >
                  <!-- Learn more... -->
                  <template v-slot:default="slotProps">
                    {{ slotProps.togglerText.display }}
                  </template>
                </ToggleControl>
                <b-collapse id="specialProjectCodeText">
                  <SpecialProjectCodeText />
                </b-collapse>
              </div>
              <b-form-input
                id="specProjCode"
                :maxlength="fieldMaxLength('specProjCode')"
                placeholder="Special Project Code"
                @input="fieldOnInput('specProjCode')"
                v-model="form.specProjCode.value"
                :state="form.specProjCode.valid"
                :disabled="form.specProjCode.disabled"
                trim
              ></b-form-input>
            </b-collapse>
          </b-form-group>

          <!-- -------------------------------- Note  -->
          <b-form-group
            id="note-group"
            label-for="note"
            label-class="label-bold"
            :invalid-feedback="form.note.errorMsg"
            :state="form.note.valid"
          >
            <slot name="label">
              <ToggleControl toggle-id="optionalNote">
                <span class="label-bold">Note (optional)</span>
              </ToggleControl>
            </slot>
            <b-collapse id="optionalNote">
              <p class="form-text text-secondary text-smaller-1 px-3 pb-1 m-0">
                If you need or want to supply additional information about the
                image you're uploading, enter it here.
              </p>
              <p class="text-primary text-smaller-1 m-2">
                # Characters: {{ noteLength }} /
                {{ fieldMaxLength('note') }}
              </p>
              <b-form-textarea
                id="note"
                placeholder="Note..."
                rows="5"
                :maxlength="fieldMaxLength('note')"
                @input="fieldOnInput('note')"
                v-model="form.note.value"
                :state="form.note.valid"
                :disabled="form.note.disabled"
              ></b-form-textarea>
            </b-collapse>
          </b-form-group>

          <FormErrorAlert
            :showAlert="showFormActionError"
            :msg="formErrorMsg"
          />

          <button
            @click="setSubmitted"
            type="submit"
            class="btn btn-block btn-submit pap-form__btn"
            :disabled="submitDisabled"
          >
            Upload
          </button>
        </b-form>
      </div>
    </div>

    <div :class="overlayClass">
      <div class="page-overlay__content-container">
        <b-spinner label="Spinning" :variant="spinnerColor"></b-spinner>
        <div class="page-overlay__content-text">
          <!-- <div class="page-overlay__process-text-wrapper"> -->
          <p v-show="!uploadCompleted" class="mb-2">Uploading image:</p>
          <p v-show="uploadCompleted" class="mb-2">Image file uploaded!</p>
          <!-- </div> -->

          <!-- <b-progress
            :value="uploadProgress"
            :max="uploadProgressMax"
            show-progress
            animated
          ></b-progress> -->

          <b-progress
            :max="uploadProgressMax"
            show-progress
            animated
            height="1.3rem"
            :variant="spinnerColor"
          >
            <b-progress-bar
              :value="uploadProgress"
              :label="`${parseInt(uploadProgress, 10)}%`"
            ></b-progress-bar>
          </b-progress>

          <div class="page-overlay__process-text-wrapper">
            <p v-show="uploadCompleted">
              Just a moment - your upload is processing!
            </p>
            <p v-show="uploadCompleted" mt-2>{{ periods }}</p>
          </div>
        </div>
      </div>
    </div>

    <TOSModal />
    <ImageGuideModal />
  </div>
</template>

<script>
console.log(Math.random());
// Get associated thumbnail file name for a given image filename
// and size:  g1.jpg -->> 150px thumb filename = g1_150x150.jpg
function thumbFilename(sourceFilename, size) {
  const fileNameSizeSegment = `_${size}x${size}`;
  const re = /(\.\w+$)/;
  return sourceFilename.replace(re, fileNameSizeSegment + '$1');
}

// Polling to get image URL
function getImageUrl(firebaseApi, imagePath) {
  var maxNumberOfTries = 10;
  var timeBetweenTries = 1000; // interval, in milliseconds

  async function getUrl(trackerObj) {
    trackerObj.count++;
    // console.log('imagePath:', imagePath);
    // console.log('getUrl: Number of tries:', trackerObj.count);
    var imageUrl = await firebaseApi.getImageUrl(imagePath);
    // console.log('imageUrl:', imageUrl);
    if (imageUrl !== 'error') {
      trackerObj.res(imageUrl);
    } else {
      if (trackerObj.count < maxNumberOfTries) {
        setTimeout(getUrl, timeBetweenTries, trackerObj);
      } else {
        trackerObj.res(false);
      }
    }
  }
  // Always resolves: Resolves to imageUrl if/when url is obtained; resolves to false if not (as opposed to rejecting with value false)
  return new Promise((res, rej) => {
    var fileExistsTracker = {
      count: 0,
      result: false,
      res,
    };
    setTimeout(getUrl, 500, fileExistsTracker);
  });
}

import TOSModal from '@/components/modals/TermsOfService';
import ImageGuideModal from '@/components/modals/ImageGuide';

import AlertCard from '@/components/notifications/AlertCard';
import CardImage from '@/components/cards/CardImage';

import FormTitle from '@/components/form/FormTitle.vue';
import ImageFileField from '@/components/form/FormImageFileField';
import SelectBreed from '@/components/form/FormSelectListBreeds';
import SelectCountry from '@/components/form/FormSelectListCountries';
import SelectState from '@/components/form/FormSelectListStates';
import ProvinceField from '@/components/form/FormProvinceField';
import CaptionSection from '@/components/form/FormCaptionSection';
import SpecialProjectCodeText from '@/components/text/SpecialProjectCode';

import FormErrorAlert from '@/components/form/FormErrorAlert.vue';

import ToggleControl from '@/components/layout/ToggleControl';

import { Upload } from '@/models/Upload';

import { mapGetters } from 'vuex';

// import Observable from '@/lib/Observable';

var imageUploadResultDocListener; // Store the doc listener to call at end to detach

export default {
  name: 'PageUploadImage',
  components: {
    TOSModal,
    ImageGuideModal,
    ImageFileField,
    SelectBreed,
    SelectCountry,
    SelectState,
    ProvinceField,
    CaptionSection,
    SpecialProjectCodeText,
    AlertCard,
    CardImage,
    FormTitle,
    FormErrorAlert,
    ToggleControl,
  },
  data: function () {
    return {
      agreeTerms: false,
      caption: '',

      // buttons
      formFillDisabled: true,
      submitDisabled: true,

      // selectedImageSrc: 'https://source.unsplash.com/random/600x800',
      pageOverlayHidden: true,
      tosPageOverlayHidden: false,

      // uploadProgressVal: 0,
      uploadProgressMax: 100,
      uploadCompleted: false,
      periods: '',
      maxNumPeriods: 30, // periods: ... etc. This is for the 'growing periods line' loader in the image-processing phase of the upload process
      timerId: 0,

      // uploadResultListenerInitialized: false, // not needed ... delete? pretty sure...
      resultStatus: '',
      resultStatusMsg: '',
      resultTimestamp: 0,
      resultUploadId: '',
      uploadResultArrived: false,

      // Final result properties, re: handle/fill the AlertCard that displays when upload ends
      showUploadResult: false,
      resultType: '',
      resultSuccessMsg: 'Image uploaded.',
      resultErrorMsg: '',

      // The upload data model, i.e. this is what's populated via user form data and other programmatically generated data; this is
      // source of data for document in imageUploads collection.
      upload: null,

      image: {
        file: null,
        filename: '',
        src: '',
        size: '',
        height: '',
        width: '',
      },

      // Set after successful upload; used as prop (named 'image') in CardImage component which is being used as a success alert.
      // THAT comp expects and 'image' prop, which is an obj.
      uploadedImage: {
        docStatus: 'ok',
        creTime: Date.now(),
        dogName: '',
        breed: '',
        caption: '',
        imageThumbURL: '',
      },
      // https://firebasestorage.googleapis.com/v0/b/book-images-mgr-dev.appspot.com/o/dogimgs%2F96rvnjhLgkTHguV6L0PP16k5w5J2%2FdogCat--Depositphotos_34909067_l-2015.jpg?alt=media&token=d77d0241-9acb-425c-b146-103638e1a8b0
      // Form data
      form: {
        imageFilename: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        dogName: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        breedId: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        breedDesc: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        country: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        state: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        province: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        city: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        fName: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        lName: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        captionLastName: {
          value: 'full',
        },
        specProjCode: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
        note: {
          value: '',
          disabled: true,
          valid: null,
          errorMsg: '',
        },
      },
      // Text of any overall form error, e.g. validation error, submission result error, etc.
      formErrorMsg:
        'Ooops! Please correct the error(s) shown in the form above, then click Upload again.',

      // Form validation: true: Every field has a valid value; false: At least one field has an invalid value
      // formValid: false,
      fieldsValid: false,
      // Image file validation: true: Image file passes all validation (packaged in the FormImageFileField component); false; validation failed.
      imageValid: false,

      // Form submission result: Result of form submission (when validated).
      // true: Result of submitted form action was an error; false: Initial state - no error (since form
      // not submitted yet); methods can "reset" this to false to simulate initial state any time after a
      // form submission.
      formError: false,

      // Flag: true: Form submit button was clicked; false: Reset to primitive state, i.e. form not submitted
      formSubmitted: false,

      componentKey: '',

      // xx: {},
    };
  },
  computed: {
    ...mapGetters('uploadTracker', ['imageCounts']),
    ...mapGetters(['uploadProgress', 'userProfile']),
    ...mapGetters('auth', ['authUser']),

    showPage() {
      if (
        typeof this.numPending === 'number' &&
        typeof this.maxNumUploads === 'number'
      ) {
        return true;
      }
      return false;
    },

    noteLength: function () {
      return this.form.note.value.length;
    },

    maxNumUploads: function () {
      return this.imageCounts.maxNumPending; // || 0;
    },

    numPending: function () {
      return this.imageCounts.numSubmitted + this.imageCounts.numPending; // || 0;
    },

    showMaxUploadsReachedAlert: function () {
      // This if stmt ... so that BOTH this and uploadResult won't both show at same time.
      if (!this.showUploadResult) {
        return this.numPending >= this.maxNumUploads ? true : false;
      }
      return false;
    },

    spinnerColor: function () {
      return this.uploadCompleted ? 'secondary' : 'primary';
    },

    showForm: function () {
      // console.log(this.showMaxUploadsReachedAlert);
      return !(this.showUploadResult || this.showMaxUploadsReachedAlert);
      // return !this.showUploadResult;
    },

    showFormActionError: function () {
      return (this.formSubmitted && !this.formValid) || this.formError;
    },
    // page-overlay page-overlay--hide
    overlayClass: function () {
      return {
        'page-overlay': true,
        'page-overlay--hide': this.pageOverlayHidden,
      };
    },

    tosOverlayClass: function () {
      return {
        'page-overlay': true,
        'page-overlay--hide': this.tosPageOverlayHidden,
      };
    },

    // Form validation: true: Every field has a valid value; false: At least one field has an invalid value
    formValid: function () {
      return this.fieldsValid && this.imageValid;
    },

    resultHeader: function () {
      if (this.resultType === 'success') {
        return 'Success!';
      }
      if (this.resultType === 'error') {
        return 'There was a problem...';
      }
      return '';
    },
    resultMessage: function () {
      return this.resultErrorMsg ? this.resultErrorMsg : this.resultSuccessMsg;
    },

    // **JUST** the result of the image upload, NOT the entire 'upload process', i.e.
    // the image upload is Step 1 of a multi-part process
    // ******* NOT NEEDED NOW?????????????????????????????????????????
    imageUploadResult: function () {
      return [
        this.resultStatus,
        this.resultTimestamp,
        this.resultUploadId,
      ].join(' ');
    },
  },
  watch: {
    resultTimestamp: function (newVal, oldVal) {
      // if (!this.resultWatchEnabled) return;
      console.log('resultStatus changed:', newVal, oldVal);
      // if (this.uploadResultListenerInitialized) {

      if (oldVal !== 0 && newVal > oldVal) {
        this.uploadResultArrived = true;
        // this.xx.uploadResultArrived = true;
        // console.log('RRRRR', this.imageUploadResult);
        // this.hideOverlay();

        // Update state with value of file upload result
        this.$store.dispatch('setUploadStatus', this.resultStatus);

        this.processUpload();
      }
      // }
    },

    uploadProgress: function (newVal, oldVal) {
      if (newVal === 100) {
        setTimeout(() => {
          this.uploadCompleted = true;
          this.startPeriods();
        }, 500);
      }
    },
  },
  methods: {
    // openTOS: function () {
    //   this.showTosOverlay();
    // },
    showOverlay: function () {
      this.pageOverlayHidden = false;
      document.body.classList.add('page-overlay-showing');
    },
    hideOverlay: function () {
      this.pageOverlayHidden = true;
      document.body.classList.remove('page-overlay-showing');
    },

    fieldMaxLength: function (fieldName) {
      return Upload.getPropTruncateLength(fieldName) || 50;
    },

    // showTosOverlay: function () {
    //   this.tosPageOverlayHidden = false;
    //   document.body.classList.add('page-overlay-showing');
    // },
    // hideTosOverlay: function () {
    //   this.tosPageOverlayHidden = true;
    //   document.body.classList.remove('page-overlay-showing');
    // },

    // Handler for agreeTerms checkbox change
    toggleAgreeTerms: function () {
      this.agreeTerms = !this.agreeTerms;
      Object.keys(this.form).forEach((field) => {
        // console.log(field);
        this.form[field].disabled = !this.agreeTerms;
      });

      // Toggle one-click fill form button disabled state
      this.formFillDisabled = !this.agreeTerms;

      // Toggle submit button disabled state
      this.submitDisabled = !this.agreeTerms;
    },
    setSubmitted: function () {
      this.formSubmitted = true;
    },
    resetSubmitted: function () {
      this.formSubmitted = false;
    },

    // // Event listener from breeds list component: Did the breeds list load properly?
    // breedsFetched: function (didFetch) {
    //   console.log('HHHHHHHHHHHEY!');
    //   console.log('breedsFetched', didFetch);
    //   if (!didFetch) {
    //     this.breedsListError.isError = true;
    //   } else {
    //     this.loadingSuccess = true;
    //   }
    // },

    setField: function (fieldValue, fieldName) {
      // if (fieldName === 'province') {
      //   console.log(fieldName, 'FIELD VALUE:', fieldValue);
      // }
      this.form[fieldName].value = fieldValue;
      // if (
      //   (fieldName === 'country' && fieldValue === 'US') ||
      //   fieldValue === ''
      // ) {
      //   this.form.province.value = '';
      //   this.setComponentKey(); // Trigger rerender of province field component so that value of '' shows in the input element
      // }
      this.fieldOnInput(fieldName);
    },

    // Special case of 'setField' for the breedId field
    // Event value passed from SelectBreed child component is an object: { breedId: breedId, breed: name-of-breed}
    setBreed: function (breedData) {
      this.form.breedId.value = breedData.breedId;
      if (breedData.breedId !== 'mixed') {
        this.form.breedDesc.value = '';
      }
      this.fieldOnInput('breedId');
    },

    // For given field fieldName, on input to the field, i.e. when the value changes, reset the
    // form state to its primitive/original state in terms of form error alerts, form field error alerts, etc.
    fieldOnInput: function (fieldName) {
      // console.log('field value changed on:', fieldName);
      this.resetSubmitted(); // Reset 'form submitted' flag to false, i.e. "starting over" as tho form was never submitted.
      this.formError = false;
      if (this.form[fieldName].valid === false) {
        this.form[fieldName].valid = null; // Set to null to not show validation state feedback on the field, i.e. NO red/green styles
        // console.log('test method called on field', fieldName);
        // if (fieldName === 'country') {
        //   this.form.province.value = '';
        //   this.setComponentKey(); // Trigger rerender of province field component so that value of '' shows in the input element
        // }
      }
    },

    // Param: imgDataObj: { image: image object, validationError:'Image validation error string' }
    imageSelected: function (imgDataObj) {
      console.log('Image Selected Data Obj:', imgDataObj);
      this.imageValid = false;
      this.image = imgDataObj.image;
      this.form.imageFilename.value = imgDataObj.image.filename;
      if (imgDataObj.validationError) {
        this.form.imageFilename.valid = false; // Set to false to show validation state feedback of 'invalid', i.e. red styles
        this.form.imageFilename.errorMsg = imgDataObj.validationError;
      } else {
        this.imageValid = true;
        this.fieldOnInput('imageFilename');
      }
    },

    // imageSelected: function (imageObj) {
    //   this.image = imageObj;
    //   this.form.imageFilename.value = imageObj.filename;
    //   this.fieldOnInput('imageFilename');
    // },

    generatePeriods: function () {
      var currentPeriods = this.periods;
      if (currentPeriods.length <= this.maxNumPeriods) {
        this.periods = currentPeriods + '.';
      } else {
        this.periods = '';
      }
    },
    startPeriods: function () {
      this.timerId = setInterval(this.generatePeriods, 200);
    },
    stopPeriods: function () {
      clearInterval(this.timerId);
    },
    doSelectedImage: function (e) {
      var selectedFile = e.target.files[0];
      // console.dir(selectedFile);
      if (!selectedFile) return;
      var objUrl = URL.createObjectURL(selectedFile);
      this.image.src = objUrl;
      this.image.size = selectedFile.size;
      this.image.filename = selectedFile.name;
      this.form.imageFilename.value = selectedFile.name;
      const img = document.createElement('img');
      img.src = objUrl;
      var vm = this;
      img.onload = function () {
        var { width, height } = this;
        vm.image.height = height;
        vm.image.width = width;
        URL.revokeObjectURL(this.src);
      };
      console.log(this.image);
    },

    // setTrackerDocListener: function (store, trackerDocRef, trackerDoc) {
    //     var trackerDocListenerCb = imageStats(store, trackerDocRef, trackerDoc);
    //     console.log('the listener callback:', trackerDocListenerCb)
    //     var trackerDocListener = trackerDocRef.onSnapshot(trackerDocListenerCb);
    //     store.dispatch('setDocListener', {
    //         imageCounts: trackerDocListener,
    //     });
    // }
    getUploadResult: function () {
      // console.log('LINE 432 this', this);
      // THIS WORKS
      // return new Promise((res, rej) => {
      //   this.xx = Observable.create(() => {
      //     console.log('THIS inside promise', this);
      //     res(true);
      //   });
      // });
    },

    fillForm: function () {
      for (var fieldName in this.userProfile) {
        // console.log(fieldName);
        if (this.form[fieldName] !== undefined) {
          // Check that user profile value for the field has a non-empty value; otherwise, if field already in validation error
          // state, the call to fieldOnInput resets it (clearing the error state, i.e. red border and error text); user wouldn't
          // see error again on the empty field until clicking submit again, i.e. what we want is for the error state to persist
          // so user still sees it (just like when manually filling an error'd field -- the other error'd fields maintain their
          // error state).
          if (this.userProfile[fieldName] !== '') {
            this.form[fieldName].value = this.userProfile[fieldName];
            this.fieldOnInput(fieldName);
          }
        }
      }
      this.setComponentKey();
    },
    setComponentKey: function () {
      this.componentKey = Date.now();
    },
    validate: function () {
      // import { Upload } from '@/models/Upload';
      // var { Upload } = require('@/models/Upload');

      var formData = {};
      Object.keys(this.form).forEach((fieldName) => {
        formData[fieldName] = this.form[fieldName].value;
      });

      formData.specProjCode = formData.specProjCode.toLowerCase();
      var upload = Upload.set(formData); // Validation here so, test if validation passed next...

      console.log('upload data', upload);
      // this.formValid = Upload.validationPassed();
      this.fieldsValid = Upload.validationPassed();

      // Reset form field values to display latest 'state' of form after validation/sanitization/etc.
      Object.keys(upload).forEach((fieldName) => {
        this.form[fieldName].value = upload[fieldName];
      });

      // if (!this.formValid) {
      if (!this.fieldsValid) {
        // this.formErrorMsg =
        //   'Ooops! Please correct the errors shown in the form above, then click Upload again.';
        Upload.validationErrors().forEach((err) => {
          var propName = Upload.getErrorProp(err);
          // console.log('prop', propName);
          this.form[propName].valid = false; // Set to false to show validation state feedback of 'invalid', i.e. red styles
          this.form[propName].errorMsg = Upload.getErrorMsg(err);
          // console.log(propName, Upload.getErrorMsg(err));
        });
      }
      this.upload = upload;
    },

    createUploadResultDocListener: function () {
      var resultDocRef = this.$fapi.db
        .collection('uploadResults')
        .doc(this.authUser.uid);

      // Var defined top of file as 'global' since need access in another method (and didn't need to attach it
      // to vue instance ... no need). Will call this at final step of upload to detach the listener.
      imageUploadResultDocListener = resultDocRef.onSnapshot((doc) => {
        var { status, statusMsg, timestamp, uploadId } = doc.data();
        console.log(status, statusMsg, timestamp, uploadId);
        // Initially (before the first upload), uploadId is not defined
        // on the upload result doc.
        // If uploadId isn't defined, set it to empty str since we're setting
        // instance properties next and this is one of them.
        if (!uploadId) {
          uploadId = '';
        }
        // Set local instance props to match doc values, i.e. sync up the
        // doc with the instance
        this.resultStatus = status;
        this.resultStatusMsg = statusMsg;
        this.resultTimestamp = timestamp;
        this.resultUploadId = uploadId;
      });
    },

    ui_onUploadEnd: function () {
      this.hideOverlay();
      this.showUploadResult = true;
      // this.$router.push('/upload-result');
    },

    doUpload: async function () {
      // In the state, reset (i.e. set to zero) the number of upload tracker doc changes, insuring we start this upload 'cleanly' in terms
      // of that number (since there are TWO doc changes in a new upload and at that point, the doc listener takes certain action).
      this.$store.dispatch('resetNumUploadTrackerChanges');
      // console.log(
      //   'num tracker changes from created()',
      //   this.$store.state.numUploadTrackerChanges
      // );

      // this.$router.push('/upload-result');
      // return;

      // var sref = this.$fapi.storage.ref(
      //   'dogimgs/' + this.authUser.uid + '/' + 'lost-in-space-robot.jpg'
      // );
      // console.log('storage ref', sref);
      // await sref.updateMetadata({
      //   customMetadata: {
      //     h: '1000test',
      //     w: '3000test',
      //   },
      // });
      // console.log('done metadata');
      // return;
      // --------------- Validate form data
      this.validate();
      if (!this.formValid) return;

      // console.log('TEMP STOP...');
      // return;

      // Add in computed property 'caption' to formData
      // this.upload.caption = this.caption;
      // console.log('caption', this.caption);

      // console.log('UPLOAD obj', Upload.getSnapshot(this.upload));
      // return;
      // --------------- Begin main processing: Image upload
      this.$store.dispatch('setUploadInProgress', true);
      this.showOverlay();
      // return;
      this.createUploadResultDocListener();

      // await this.$fapi.testUploadProgress(this.$store);

      // Create listener on 'uploadResults' doc
      // var uid = this.authUser.uid;

      // -------- END imageUploadResult doc listener setup -------------------------

      // 2. AWAIT image upload
      var metaData = {
        h: this.image.height,
        w: this.image.width,
        filename: this.image.filename,
        atos: true,
      };

      // await this.$fapi.uploadImage(
      //   'dogimgs/' + this.authUser.uid,
      //   this.image.file,
      //   this.$store,
      //   metaData
      // );

      // return;

      // Note: Setting metadata here means the thumbnail img created after upload also gets this same metadata... but that's ok.
      try {
        await this.$fapi.uploadImage(
          'dogimgs/' + this.authUser.uid,
          this.image.file,
          this.$store,
          metaData
        );
      } catch (err) {
        // err is string here
        // uploadError = true;
        console.log('UPLOAD ERROR: Type: Executing Upload:', err);
        // this.formError = true;
        // this.formErrorMsg =
        this.$store.dispatch('setUploadStatus', 'e_upload_exec_error');
        this.$store.dispatch('setUploadInProgress', false);
        this.$store.dispatch('setUploadCompleted', true);
        this.resultType = 'error';
        this.resultErrorMsg = 'Image upload failed: ' + err;
        this.ui_onUploadEnd();
      }
    },

    // Called after the 'upload' method, once the upload result has arrived (see in 'watch').
    processUpload: async function () {
      if (this.resultStatus !== 'ok') {
        // this.formError = true;
        // this.formErrorMsg = this.resultStatus;
        this.$store.dispatch('setUploadInProgress', false);
        this.$store.dispatch('setUploadCompleted', true);
        this.resultType = 'error';
        this.resultErrorMsg = 'Image upload failed: ' + this.resultStatusMsg;
        this.ui_onUploadEnd();
        return;
      }

      // var { Upload } = require('@/models/Upload');

      // try {
      //   console.log('Setting image metadata...');
      //   var storageRef = this.$fapi.storage.ref(
      //     'dogimgs/' + this.authUser.uid + '/' + this.image.filename
      //   );
      //   await storageRef.updateMetadata({
      //     customMetadata: {
      //       h: this.image.height,
      //       w: this.image.width,
      //     },
      //   });
      //   console.log('Metadata finished');
      // } catch (err) {
      //   // uploadError = true;
      //   console.log('UPLOAD ERROR: Type: File metadata:', err);
      //   this.formError = true;
      //   this.formErrorMsg = 'UPLOAD ERROR: File set metadata failed: ' + err;
      // }

      // NO FILE UPLOAD error at this point so: Need to update/finalize imageUploads document associated
      // with THIS upload.  Get that doc ID from the uploadResult document.
      var uid = this.authUser.uid;
      var uploadId = this.resultUploadId;
      var imageUploadRef = this.$fapi.getDocRefById('imageUploads', uploadId);

      // 1. Get URLs of main image and thumbnail image
      var imageFilename = this.image.filename;
      var uploadDoc = await imageUploadRef.get();
      if (uploadDoc.exists) {
        // Get main image URL -------------------------------
        var mainImgUrl = await getImageUrl(
          this.$fapi,
          'dogimgs/' + uid + '/' + imageFilename
        );
        // var mainImgUrl = await this.$fapi.getImageUrl(
        //   'dogimgs/' + uid + '/' + imageFilename
        // );
        console.log('MainUrl', mainImgUrl);

        // Get thumbnail image URL -------------------------------
        var thumbNailPath = 'dogimgs/' + uid + '/thumbs/';
        var thumbnailImgUrl = await getImageUrl(
          this.$fapi,
          thumbNailPath + thumbFilename(imageFilename, 200)
        );
        // var thumbnailImgUrl = await this.$fapi.getImageUrl(
        //   thumbNailPath + thumbFilename(imageFilename, 200)
        // );

        console.log('ThumbUrl', thumbnailImgUrl);

        this.upload.imageURL = mainImgUrl;
        this.upload.imageThumbURL = thumbnailImgUrl;

        // --------- Set other upload properties (streamline this???):

        // serverTimestamp
        // (per firestore rules, absolutely NEED this to allow update; update fails without this (helps deter rogue user
        // who is not aware of this))
        this.upload.updatedAt = this.$fapi.firebase.firestore.FieldValue.serverTimestamp();

        // Usage status
        this.upload.usageStatus = 'pending';

        // Breed
        // this.upload.breed = this.$store.state.breeds[this.form.breedId.value];
        this.upload.breed = this.$store.state.breeds[
          this.form.breedId.value
        ].breedActual;
        this.upload.breedGeneral = this.$store.state.breeds[
          this.form.breedId.value
        ].breedGeneral;

        // Caption
        this.upload.caption = this.caption;

        // Agree-to-terms
        this.upload.agreeTerms = this.agreeTerms;

        console.log('UPLOAD OBJ:', this.upload);

        // ------- End setting other upload properties

        // Set values used in success alert message card
        this.uploadedImage.imageThumbURL = thumbnailImgUrl;
        this.uploadedImage.dogName = this.form.dogName.value;
        this.uploadedImage.breed = this.upload.breed;
        this.uploadedImage.caption = this.upload.caption;

        var mainErrorMessagePrefix = 'Upload failed: ';
        var mainErrorMessage = `There was a problem with this upload - the process did not complete properly. 
        You should DELETE this upload (on the Uploads list page), then try uploading again.`;

        // Do the db doc update
        // await imageUploadRef.update(Upload.getSnapshot(this.upload));
        // console.log(Upload.getSnapshot(this.upload));
        try {
          // This update of the imageUpload doc will throw error due to fb security rules: Existing imageUpload doc
          // MUST have both image URLs populated; if either one is empty --- AND one/both of the image URLS fetched above
          // is malfomred, i.e. value is 'error, i.e. doesn't match allowed characters format of a URL ---  it's a signal that
          // either image upload (orig or a prev replacement) did not go to completion. If THAT'S the
          // case, user is notified to delete this upload.
          await imageUploadRef.update(Upload.getSnapshot(this.upload));
          this.resultType = 'success';
        } catch (err) {
          this.resultType = 'error';
          this.resultErrorMsg =
            mainErrorMessagePrefix + mainErrorMessage + ' Error Code: PE'; // err.message;
          // Since the imageUpload update in the try block imm. above failed, that update doesn't occur; therefore, the uploadsTracker
          // doc update doesn't occur and so the listener function on that doc doesn't fire leaving the uploadTracker number of
          // changes at one (1) -- never gets to two so the setUploadProgress state prop stays at true; so, force it to false here
          // (so that UI state is proper, e.g. show the 'Delete' button on each image card, etc.).
          this.$store.dispatch('setUploadInProgress', false);
        }
      }

      // Detach listener
      //imageUploadResultDocListener();

      // Set upload progress back to zero in state
      this.$store.dispatch('setUploadProgress', 0);

      // Signal that upload process has completed
      this.$store.dispatch('setUploadCompleted', true);

      // Handle UI
      this.ui_onUploadEnd();
    },
  },

  // mounted: function () {
  //   this.componentLoaded = true;
  // },
  created: function () {
    this.componentLoaded = true;

    this.$store.dispatch('setUploadProgress', 0);
    this.$store.dispatch('setUploadStatus', '');
    this.$store.dispatch('setUploadInProgress', false);
    this.$store.dispatch('setUploadCompleted', false);
    // this.$store.dispatch('resetNumUploadTrackerChanges');
    // console.log(
    //   'num tracker changes from created()',
    //   this.$store.state.numUploadTrackerChanges
    // );
  },
};
</script>

<style>
</style>